// Клиент `/api/v1/settings/branch-name-template*`.
//
// Контракт backend'а — pydantic-схемы
// `backend/modules/settings/http/template_schemas.py`. Имена полей берём
// 1:1: org-уровень отдаёт `org_template` / `effective_org_template` /
// `org_template_invalid_reason` (почему сохранённый org-дефолт не проходит
// сегодняшние правила; null — проходит) / `default_template` / `presets` /
// `placeholders`; per-repo override — `template` (или null = наследует
// org) и `template_invalid_reason` (то же для override). PUT принимает
// `{ template }`. Сохранённое значение, не проходящее правила, отдаётся
// 200-ответом с причиной, а не ошибкой — см.
// docs/decisions/0079-branch-template-stored-value-failing-rules-served-with-reason.md.
// JWT прикручивает `authedFetch`. Невалидный шаблон → 422 с человеко-
// читаемым `detail`.

import { readErrorDetail } from "../../api/errorDetail";
import { authedFetch } from "../../auth/authedFetch";

// HTTP-код «шаблон не прошёл серверную валидацию» (тело с `detail`).
export const HTTP_UNPROCESSABLE_ENTITY = 422;

const BASE = "/api/v1/settings/branch-name-template";

// Один поддерживаемый плейсхолдер из легенды. `is_task_id` помечает
// токены уникального id задачи — шаблон обязан содержать хотя бы один.
export interface PlaceholderInfo {
  readonly name: string;
  readonly is_task_id: boolean;
}

// Org-состояние настроек + легенда (ответ GET/PUT/DELETE org-уровня).
export interface BranchTemplateSettingsResponse {
  readonly org_template: string | null;
  readonly effective_org_template: string;
  // Причина, по которой сохранённый org-дефолт не проходит сегодняшние
  // правила записи (null — проходит или не задан). При не-null
  // `org_template` — сохранённое значение текстом, а
  // `effective_org_template` — заводской шаблон.
  readonly org_template_invalid_reason: string | null;
  readonly default_template: string;
  readonly presets: readonly string[];
  readonly placeholders: readonly PlaceholderInfo[];
}

// Per-repo override (или null = репозиторий наследует org-дефолт).
export interface RepoBranchTemplateResponse {
  readonly template: string | null;
  // Причина невалидности сохранённого override (null — проходит или не
  // задан). При не-null задачи репозитория получают заводской шаблон, а
  // не org-дефолт.
  readonly template_invalid_reason: string | null;
}

// Body PUT-эндпоинтов (org-дефолт и repo-override одинаковы).
export interface SetBranchTemplateRequest {
  readonly template: string;
}

/**
 * HTTP-ошибка с числовым `status`. Секция различает 422 (невалидный
 * шаблон, `detail` в теле) от прочих кодов. Зеркало `trustRules.HttpError`.
 */
export class HttpError extends Error {
  public readonly status: number;
  public readonly detail: string | null;
  public constructor(status: number, message: string, detail: string | null) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.detail = detail;
  }
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// Бросить HttpError с `detail` тела отказа: FastAPI кладёт текст
// исключения валидатора в `detail` строкой.
async function throwHttp(response: Response, label: string): Promise<never> {
  const detail = await readErrorDetail(response);
  throw new HttpError(
    response.status,
    `${label}: HTTP ${String(response.status)}`,
    detail,
  );
}

export async function fetchOrgTemplate(): Promise<BranchTemplateSettingsResponse> {
  const response = await authedFetch(BASE, { headers: buildHeaders() });
  if (!response.ok) {
    await throwHttp(response, "fetchOrgTemplate");
  }
  return (await response.json()) as BranchTemplateSettingsResponse;
}

export async function setOrgTemplate(
  body: SetBranchTemplateRequest,
): Promise<BranchTemplateSettingsResponse> {
  const response = await authedFetch(BASE, {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    await throwHttp(response, "setOrgTemplate");
  }
  return (await response.json()) as BranchTemplateSettingsResponse;
}

export async function resetOrgTemplate(): Promise<BranchTemplateSettingsResponse> {
  const response = await authedFetch(BASE, {
    method: "DELETE",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    await throwHttp(response, "resetOrgTemplate");
  }
  return (await response.json()) as BranchTemplateSettingsResponse;
}

function repoPath(repoFullName: string): string {
  // `owner/repo` уходит в путь как есть — оба сегмента уже
  // URL-безопасны (имена репозиториев Forgejo не содержат `/` внутри
  // сегмента); раздельное кодирование сломало бы слеш-разделитель.
  return `${BASE}/repositories/${repoFullName}`;
}

export async function fetchRepoTemplate(
  repoFullName: string,
): Promise<RepoBranchTemplateResponse> {
  const response = await authedFetch(repoPath(repoFullName), {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    await throwHttp(response, "fetchRepoTemplate");
  }
  return (await response.json()) as RepoBranchTemplateResponse;
}

export async function setRepoTemplate(
  repoFullName: string,
  body: SetBranchTemplateRequest,
): Promise<RepoBranchTemplateResponse> {
  const response = await authedFetch(repoPath(repoFullName), {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    await throwHttp(response, "setRepoTemplate");
  }
  return (await response.json()) as RepoBranchTemplateResponse;
}

export async function resetRepoTemplate(
  repoFullName: string,
): Promise<RepoBranchTemplateResponse> {
  const response = await authedFetch(repoPath(repoFullName), {
    method: "DELETE",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    await throwHttp(response, "resetRepoTemplate");
  }
  return (await response.json()) as RepoBranchTemplateResponse;
}
